<script lang="ts">
	import { onMount } from 'svelte';
	import {
		CONFIG,
		createDataStreams,
		createParticles,
		drawDataStreams,
		updateAndDrawParticles,
		drawConnections,
		trySpawnPulse,
		updateAndDrawPulses,
		drawMouseGlow,
		drawAmbientOrbs
	} from './particle-network';
	import type { Particle, DataPulse, DataStream } from './particle-network';
	import { prefersReducedMotion, isMobileDevice } from '$lib/utils/motion';

	let canvas: HTMLCanvasElement;

	onMount(() => {
		// Skip animation if user prefers reduced motion
		if (prefersReducedMotion()) {
			return;
		}

		const ctx = canvas.getContext('2d');
		if (!ctx) return;

		const isMobile = isMobileDevice();
		// Reduce particle density on mobile for better performance
		const particleDensityMultiplier = isMobile ? 2 : 1;

		let width: number;
		let height: number;
		let particles: Particle[] = [];
		let pulses: DataPulse[] = [];
		let dataStreams: DataStream[] = [];
		let mouseX = -1000;
		let mouseY = -1000;
		let animationId = 0;
		let time = 0;
		let lastPulseTime = 0;
		let frameCount = 0;
		let isVisible = true;

		const resize = () => {
			width = canvas.offsetWidth;
			height = canvas.offsetHeight;
			canvas.width = width * window.devicePixelRatio;
			canvas.height = height * window.devicePixelRatio;
			ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
			// Create fewer particles on mobile
			particles = createParticles(width, height, particleDensityMultiplier);
			dataStreams = createDataStreams(width, height);
		};

		const handleMouseMove = (e: MouseEvent) => {
			const rect = canvas.getBoundingClientRect();
			mouseX = e.clientX - rect.left;
			mouseY = e.clientY - rect.top;
		};

		const handleMouseLeave = () => {
			mouseX = -1000;
			mouseY = -1000;
		};

		const animate = () => {
			if (!isVisible) {
				animationId = 0;
				return;
			}

			// On mobile, skip every other frame for better performance
			frameCount++;
			if (isMobile && frameCount % 2 !== 0) {
				animationId = requestAnimationFrame(animate);
				return;
			}

			ctx.clearRect(0, 0, width, height);
			time += CONFIG.TIME_STEP;

			// Draw flowing data streams
			drawDataStreams(ctx, dataStreams, width, height, time);

			// Spawn new pulses periodically (less frequently on mobile)
			const pulseInterval = isMobile ? CONFIG.PULSE_INTERVAL * 2 : CONFIG.PULSE_INTERVAL;
			if (time - lastPulseTime > pulseInterval && particles.length > 1) {
				lastPulseTime = time;
				trySpawnPulse(particles, pulses);
			}

			// Update and draw particles
			updateAndDrawParticles(ctx, particles, width, height, mouseX, mouseY, time);

			// Skip expensive O(n²) connection drawing on mobile
			if (!isMobile) {
				drawConnections(ctx, particles);
			}

			// Draw and update pulses (skip on mobile - they depend on connections)
			if (!isMobile) {
				pulses = updateAndDrawPulses(ctx, pulses, particles);
			}

			// Skip mouse glow on mobile (no mouse)
			if (!isMobile) {
				drawMouseGlow(ctx, mouseX, mouseY);
			}

			// Skip ambient orbs on mobile (expensive gradients)
			if (!isMobile) {
				drawAmbientOrbs(ctx, width, height, time);
			}

			animationId = requestAnimationFrame(animate);
		};

		// Pause when off-screen
		const observer = new IntersectionObserver(
			([entry]) => {
				isVisible = entry.isIntersecting;
				if (isVisible && !animationId) {
					animationId = requestAnimationFrame(animate);
				}
			},
			{ threshold: 0 }
		);
		observer.observe(canvas);

		resize();
		window.addEventListener('resize', resize);
		canvas.addEventListener('mousemove', handleMouseMove);
		canvas.addEventListener('mouseleave', handleMouseLeave);
		animate();

		return () => {
			observer.disconnect();
			cancelAnimationFrame(animationId);
			window.removeEventListener('resize', resize);
			canvas.removeEventListener('mousemove', handleMouseMove);
			canvas.removeEventListener('mouseleave', handleMouseLeave);
		};
	});
</script>

<canvas bind:this={canvas} class="absolute inset-0 w-full h-full" aria-hidden="true"></canvas>
